import * as borogove from "borogove";
import {
	AddUserButton,
	Button,
	Search,
	Sidebar,
} from "@chatscope/chat-ui-kit-react";
import { Badge, CircularProgress, Dialog } from "@mui/material";
import {
	useNavigate,
	useSearchParams,
	useOutletContext,
	Await,
} from "react-router";
import Linkify from "linkify-react";
import { useQueryState } from "nuqs";
import { useEffect, useRef, useState, Suspense } from "react";
import UnsplashPicker, { InsertIntoApplicationUploader } from "unsplash-react";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
	faThumbtack,
	faThumbtackSlash,
	faCheck,
	faXmark,
} from "@fortawesome/free-solid-svg-icons";

import { AvatarWithPlaceholder } from "./AvatarWithPlaceholder";
import { AwaitOnce } from "./AwaitOnce";
import { Profile } from "./Profile";
import { href } from "../util";
import { ChannelTagsDialog } from "./ChannelTagsDialog";

export const ChatDetails = () => {
	const navigate = useNavigate();
	const [searchParams] = useSearchParams();
	const [showChooseBackground, setShowChooseBackground] = useState(false);
	const [showModerationPanel, setShowModerationPanel] = useState(false);
	const [showTagsDialog, setShowTagsDialog] = useState(false);
	const {
		activeChat,
		client,
		setPopupMember,
		selectContact,
		setWallpaper,
		persistence,
		voiceRequests,
	} = useOutletContext<{
		activeChat: borogove.Chat;
		client: borogove.Client;
		setPopupMember: (member: borogove.Member) => void;
		selectContact: (f: (contactChat: borogove.Chat) => void) => void;
		setWallpaper: (url: string) => void;
		persistence: borogove.persistence.KeyValueStore & borogove.Persistence;
		voiceRequests: Promise<borogove.Member[]>;
	}>();

	let activeChatNotifications: "all" | "mute" | "mention" | "reply" = "all";
	if (activeChat) {
		if (activeChat.notificationsFiltered()) {
			if (activeChat.notifyReply()) {
				activeChatNotifications = "reply";
			} else if (activeChat.notifyMention()) {
				activeChatNotifications = "mention";
			} else {
				activeChatNotifications = "mute";
			}
		}
	}

	const setChannelPhoto = () => {
		const input = document.createElement("input");
		input.type = "file";
		input.multiple = false;
		input.addEventListener("change", () => {
			if (!input.files || input.files.length < 1) return; // Nothing selected
			if (!(activeChat instanceof borogove.Channel)) return;

			activeChat.setPhoto(input.files[0]);
		});
		input.click();
	};

	if (!activeChat) return <div />;

	return (
		<Sidebar position="right" scrollable={false} className={"active"}>
			<Button
				icon={<FontAwesomeIcon icon={faXmark} />}
				onClick={() => {
					searchParams.delete("memberQ");
					navigate(href("..", {}, searchParams));
				}}
				style={{ float: "right" }}
				title="Close sidebar"
			/>
			<h2>Chat details</h2>
			<code>{activeChat.chatId}</code>

			{(activeChat.status.emoji || activeChat.status.text) && (
				<p className="status" title="Status">
					<span className="emoji">{activeChat.status.emoji}</span>
					<span className="text">{activeChat.status.text}</span>
				</p>
			)}

			{activeChat instanceof borogove.Channel && (
				<p className="description">
					<Linkify options={{ defaultProtocol: "https", truncate: 37 }}>
						{activeChat.description()}
					</Linkify>
				</p>
			)}

			<section>
				<h3>Settings</h3>
				<Button
					icon={
						<FontAwesomeIcon
							icon={
								activeChat.uiState === borogove.UiState.Pinned
									? faThumbtackSlash
									: faThumbtack
							}
						/>
					}
					onClick={() => activeChat.togglePinned()}
				>
					{activeChat.uiState === borogove.UiState.Pinned
						? "Unpin Chat"
						: "Pin Chat"}
				</Button>
				<Button onClick={() => activeChat.close()}>
					{activeChat instanceof borogove.Channel
						? "Leave Chat"
						: "Archive Chat"}
				</Button>
				{activeChat.canInvite() && (
					<Button
						onClick={() =>
							selectContact((contactChat) => activeChat.invite(contactChat))
						}
					>
						Invite new Member
					</Button>
				)}
				<label>
					<span>Notifications</span>
					<select
						onChange={(e) => {
							if (e.target.value === "all") {
								activeChat.setNotifications(false, true, true);
							} else if (e.target.value === "mute") {
								activeChat.setNotifications(true, false, false);
							} else if (e.target.value === "mention") {
								activeChat.setNotifications(true, true, false);
							} else if (e.target.value === "reply") {
								activeChat.setNotifications(true, true, true);
							}
						}}
					>
						<option value="all" selected={activeChatNotifications === "all"}>
							All Messages
						</option>
						<option
							value="mention"
							selected={activeChatNotifications === "mention"}
						>
							Mentions Only
						</option>
						<option
							value="reply"
							selected={activeChatNotifications === "reply"}
						>
							Mentions and Replies Only
						</option>
						<option value="mute" selected={activeChatNotifications === "mute"}>
							Mute
						</option>
					</select>
				</label>
				{activeChat.isBlocked && (
					<Button onClick={() => activeChat.unblock(true)}>Unblock</Button>
				)}
				{!activeChat.isBlocked && (
					<Button onClick={() => activeChat.block()}>Block</Button>
				)}
				{!activeChat.isBlocked && (
					<Button
						onClick={() => activeChat.block(true, activeChat.lastMessage)}
					>
						Report Spam
					</Button>
				)}
				{activeChat instanceof borogove.Channel && activeChat.canSetPhoto() && (
					<Button onClick={() => setChannelPhoto()}>Set Channel Photo</Button>
				)}
				<Button onClick={() => setShowTagsDialog(true)}>Set Tags</Button>
				<Suspense>
					<Await resolve={voiceRequests}>
						{(req) =>
							req.length > 0 && (
								<div>
									<Badge
										badgeContent={req.length}
										variant="dot"
										color="primary"
									>
										<Button onClick={() => setShowModerationPanel(true)}>
											Moderation Panel
										</Button>
									</Badge>
								</div>
							)
						}
					</Await>
				</Suspense>

				<Button onClick={() => setShowChooseBackground(true)}>
					Set Background
				</Button>

				<AwaitOnce
					key={activeChat.chatId}
					resolve={() => activeChat.members()}
					fallback={<CircularProgress />}
				>
					{(members) => (
						<MemberList
							client={client}
							setPopupMember={setPopupMember}
							chat={activeChat}
							members={members}
						/>
					)}
				</AwaitOnce>
			</section>
			{activeChat instanceof borogove.Channel && (
				<Dialog
					open={showModerationPanel}
					onClose={() => setShowModerationPanel(false)}
					id="moderation-panel"
				>
					<Suspense>
						<Await resolve={voiceRequests}>
							{(req) => (
								<>
									<h1>Voice Requests</h1>
									<ul>
										{req.map((member) => {
											const guestRole = member.roles.find(
												(r) => r.id === "none",
											);
											return (
												<li key={`member-${member.id}`}>
													<Button onClick={() => setPopupMember(member)}>
														<AvatarWithPlaceholder
															photoUri={member.photoUri}
															placeholderUri={member.placeholderUri}
															size="sm"
														/>
													</Button>
													{member.displayName}
													{guestRole &&
														activeChat.canRemoveRole(member, guestRole) && (
															<AddUserButton
																title="Make member"
																onClick={() =>
																	activeChat.removeRole(member, guestRole)
																}
															/>
														)}
													<Button
														title="Grant voice"
														icon={<FontAwesomeIcon icon={faCheck} />}
														onClick={() =>
															activeChat.voiceRequestRespond(member, true)
														}
													/>
													<Button
														title="Deny voice"
														icon={<FontAwesomeIcon icon={faXmark} />}
														onClick={() =>
															activeChat.voiceRequestRespond(member, false)
														}
													/>
												</li>
											);
										})}
									</ul>
								</>
							)}
						</Await>
					</Suspense>
				</Dialog>
			)}
			<Dialog
				open={showChooseBackground}
				onClose={() => setShowChooseBackground(false)}
				fullWidth={true}
			>
				<div
					style={{
						height:
							window.innerWidth < 600
								? (window.innerWidth * 4) / 3 + "px"
								: "700px",
						maxHeight: "80vh",
						overflowY: "auto",
					}}
				>
					<UnsplashPicker
						applicationName={import.meta.env.VITE_APP_TITLE}
						accessKey={import.meta.env.VITE_UNSPLASH_ACCESS_KEY}
						preferredSize={{ width: screen.width, height: screen.height }}
						Uploader={InsertIntoApplicationUploader}
						defaultSearch="illustration abstract texture"
						onFinishedUploading={(inurl) => {
							const url =
								inurl.replace(/(?:fm|crop|cs|q)=[^&]+&?/g, "") +
								"&auto=format&fit=crop&crop=entropy&q=70";
							persistence.set(`ui#wallpaper/${activeChat?.chatId}`, url);
							setWallpaper(url);
							setShowChooseBackground(false);
						}}
					/>
				</div>
			</Dialog>
			<ChannelTagsDialog
				open={showTagsDialog}
				client={client}
				initialTags={activeChat.getTags()}
				onClose={() => setShowTagsDialog(false)}
				onSave={(tags) => {
					activeChat.setTags(tags);
					activeChat.bookmark();
				}}
			/>
		</Sidebar>
	);
};

export const MemberList = ({
	client,
	chat,
	setPopupMember,
	members: initialMembers,
}: {
	client: borogove.Client;
	chat: borogove.Chat;
	setPopupMember: (member: borogove.Member) => void;
	members: borogove.Member[];
}) => {
	const [memberSearchQ, setMemberSearchQ] = useQueryState("memberQ");
	const [members, setMembers] = useState(initialMembers);
	useEffect(() => {
		const token = chat.addMembersUpdatedListener((updated) => {
			setMembers((existing) => {
				const updates = new Map(updated.map((m) => [m.id, m]));

				const replaced = existing.map((m) => updates.get(m.id) ?? m);

				// Append new items for now instead of re-sorting
				const haveIds = new Set(existing.map((m) => m.id));
				const appended = updated.filter((m) => !haveIds.has(m.id));

				return [...replaced, ...appended];
			});
		});

		return () => chat.removeEventListener(token);
	}, [chat]);

	const searchMembers = useRef<HTMLDivElement | null>(null);
	useEffect(() => {
		searchMembers.current?.focus();
	}, []);

	return members.length > 2 ? (
		<>
			<h3>Members</h3>
			{members.length > 15 && (
				<Search
					ref={searchMembers}
					placeholder="Search by name, ID"
					value={memberSearchQ || ""}
					onChange={(q) => setMemberSearchQ(q || null)}
					onClearClick={() => setMemberSearchQ(null)}
				/>
			)}
			<ul className="members">
				{members
					.filter(
						(member) =>
							!memberSearchQ ||
							member.displayName.includes(memberSearchQ) ||
							member.chat?.chatId?.includes(memberSearchQ),
					)
					.map((member) => (
						<li key={`member-${member.id}`}>
							<Button onClick={() => setPopupMember(member)}>
								<AvatarWithPlaceholder
									photoUri={member.photoUri}
									placeholderUri={member.placeholderUri}
									size="sm"
								/>
								{member.displayName}
							</Button>
						</li>
					))}
			</ul>
		</>
	) : (
		<>
			<h3>Profile</h3>
			{members
				.filter((p) => !p.isSelf)
				.map((p) => (
					<AwaitOnce
						key={chat.chatId}
						resolve={() => p.profile(client)}
						fallback={<CircularProgress />}
					>
						{(profile) => <Profile profile={profile} />}
					</AwaitOnce>
				))}
		</>
	);
};
